// src/components/Remove1.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { setDefaultOptions } from "date-fns";
import { et } from "date-fns/locale";
import CameraComponent from "../../components/camera";
import QRScanner from "../../components/QRScanner";
import {
  fetchMeterBrands,
  fetchQnoms,
  fetchDiameters,
  searchMeters,
  createMeterReport,
  updateMeterReport,
  uploadMeterReportImage,
} from "../../data/fetchData";
import { FaTimesCircle } from "react-icons/fa";
import { useNotification } from "../../context/NotificationContext";
import { normalizeDecimal, isValidDecimal } from "../../utils/formatters";

registerLocale("et", et);
setDefaultOptions({ locale: et });

const TYPE_OPTIONS = [
  { value: "MAIN_GAUGE", label: "Põhiarvesti" },
  { value: "SUB_GAUGE", label: "Alamarvesti" },
  { value: "WATERING", label: "Kastmine" },
];

export default function Remove1() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();

  // Form state
  const [id, setId] = useState(null);
  const [meterNumber, setMeterNumber] = useState("");
  const [estateId, setEstateId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [address, setAddress] = useState("");
  const [owner, setOwner] = useState("");
  const [brandOptions, setBrandOptions] = useState([]);
  const [brand, setBrand] = useState("");
  const [qnOptions, setQnOptions] = useState([]);
  const [qn, setQn] = useState("");
  const [diameterOptions, setDiameterOptions] = useState([]);
  const [diameter, setDiameter] = useState("");
  const [lastCertification, setLastCertification] = useState(new Date());
  const [reading, setReading] = useState("");
  const [removalDate, setRemovalDate] = useState(new Date());
  const [type, setType] = useState("");
  const [images, setImages] = useState([]);
  const [notes, setNotes] = useState("");
  const [meterId, setMeterId] = useState(null);

  // Load meter-brand, qnom and diameter lists once
  useEffect(() => {
    fetchMeterBrands()
      .then(setBrandOptions)
      .catch(() => showError("Arvesti markide laadimine nurjus."));

    fetchQnoms()
      .then(setQnOptions)
      .catch(() => showError("Qn/m³ andmete laadimine nurjus."));

    fetchDiameters()
      .then(setDiameterOptions)
      .catch(() => showError("Läbimõõtude laadimine nurjus."));
  }, [showError]);

  // Load from Works (API) or from Remove2 (draft) or initial localStorage draft or URL params
  useEffect(() => {
    // Check URL parameters first (from backend archive button)
    const urlMeterNumber = searchParams.get("meterNumber");
    const urlEstateId = searchParams.get("estateId");
    const urlClientId = searchParams.get("clientId");
    const urlAddress = searchParams.get("address");
    const urlOwner = searchParams.get("owner");
    const urlBrand = searchParams.get("brand");
    const urlQn = searchParams.get("qn");
    const urlDiameter = searchParams.get("diameter");
    const urlType = searchParams.get("type");
    const urlLastCertification = searchParams.get("lastCertification");

    if (urlMeterNumber) {
      // Populate form from URL parameters (from backend)
      setMeterNumber(urlMeterNumber);
      setEstateId(urlEstateId ? parseInt(urlEstateId) : null);
      setClientId(urlClientId ? parseInt(urlClientId) : null);
      setAddress(urlAddress || "");
      setOwner(urlOwner || "");

      // Map URL brand (model name) to brand ID - WAIT for brandOptions to load
      if (urlBrand && brandOptions.length > 0) {
        const brandMatch = brandOptions.find((b) => b.name === urlBrand);

        setBrand(brandMatch?.id?.toString() || "");
      } else {
        setBrand("");
      }

      // Map URL qn (decimal string) to QNom ID - WAIT for qnOptions to load
      if (urlQn && qnOptions.length > 0) {
        const qnMatch = qnOptions.find(
          (q) => parseFloat(q.q_nom) === parseFloat(urlQn)
        );

        setQn(qnMatch?.id?.toString() || "");
      } else {
        setQn("");
      }

      // Map URL diameter (decimal string) to Diameter ID - WAIT for diameterOptions to load
      if (urlDiameter && diameterOptions.length > 0) {
        const diameterMatch = diameterOptions.find(
          (d) => parseFloat(d.diameter) === parseFloat(urlDiameter)
        );

        setDiameter(diameterMatch?.id?.toString() || "");
      } else {
        setDiameter("");
      }

      setType(urlType || "");
      setReading(""); // Don't set reading from URL - user must enter current reading
      setLastCertification(
        urlLastCertification ? new Date(urlLastCertification) : new Date()
      );
      setRemovalDate(new Date()); // Set to today for removal

      // Clean URL parameters after loading
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
      return;
    }

    const work = location.state?.work;
    if (work) {
      // Editing existing meter-report
      if (work.meter_number !== undefined) {
        setId(work.number || work.id);
        setMeterNumber(work.meter_number.toString());
        setEstateId(work.estate);
        setClientId(work.client);
        setAddress(work.estate_address || "");
        setOwner(work.client_name || "");
        setBrand(work.meter_brand?.toString() || "");
        setQn(work.q_nom?.toString() || "");
        setDiameter(work.diameter?.toString() || "");
        // Trim unnecessary trailing zeros (e.g. 111.000 → 111, 11.100 → 11.1)
        const readingVal = work.reading != null ? Number(work.reading).toString() : "";
        setReading(readingVal);
        setRemovalDate(work.date ? new Date(work.date) : new Date());
        setType(work.type || "");
        // Handle images from backend - convert URL images to base64 if needed  
        const processImages = async (images) => {
          if (!images || !Array.isArray(images)) return [];
          
                     const imagePromises = images.map(async (img) => {
             if (typeof img === 'string') {
               // Already base64 data
               return img;
             } else if (img.data) {
               // Already base64 data in object
               return img.data;
             } else if (img.id) {
               // Use backend proxy to avoid CORS issues
               try {
                 const token = localStorage.getItem("apiToken");
                 const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/meter-report-images/${img.id}/proxy/?token=${token}`;
                 const response = await fetch(proxyUrl);
                                 if (response.ok) {
                  const blob = await response.blob();
                  // Use blob URL instead of data URL so we can detect existing images (not to re-upload)
                  return URL.createObjectURL(blob);
                }
               } catch (error) {
                 console.warn("Failed to load image via proxy:", img.id, error);
               }
             }
             return null;
           });
          
          const results = await Promise.all(imagePromises);
          return results.filter(img => img !== null);
        };
        
        processImages(work.images || []).then(setImages);
        setNotes(work.notes || "");
        setMeterId(work.meterId || null);
      } else {
        // Returning from Remove2 draft
        setMeterNumber(work.meterNumber || "");
        setEstateId(work.estate);
        setClientId(work.client);
        setAddress(work.address || "");
        setOwner(work.owner || "");
        setBrand(work.brand || "");
        setQn(work.qn || "");
        setDiameter(work.diameter || "");
        const readVal = work.reading != null ? Number(work.reading).toString() : "";
        setReading(readVal);
        setRemovalDate(
          work.removalDate ? new Date(work.removalDate) : new Date()
        );
        setType(work.type || "");
        setImages(work.images || []);
        setNotes(work.notes || "");
        setMeterId(work.meterId || null);
      }
      return;
    }
    // Initial draft from localStorage
    const draft = JSON.parse(localStorage.getItem("removeData") || "null");
    if (draft) {
      setMeterNumber(draft.meterNumber || "");
      setEstateId(draft.estate);
      setClientId(draft.client);
      setAddress(draft.address || "");
      setOwner(draft.owner || "");
      setBrand(draft.brand || "");
      setQn(draft.qn || "");
      setDiameter(draft.diameter || "");
      const draftReadVal = draft.reading != null ? Number(draft.reading).toString() : "";
      setReading(draftReadVal);
      setRemovalDate(
        draft.removalDate ? new Date(draft.removalDate) : new Date()
      );
      setType(draft.type || "");
      setImages(draft.images || []);
      setNotes(draft.notes || "");
      setMeterId(draft.meterId || null);
    }
  }, [location.state, searchParams, brandOptions, qnOptions, diameterOptions]);

  const handleSearch = async () => {
    if (!meterNumber) {
      showError("Palun sisesta arvesti number.");
      return;
    }

    // Check if we already have data from URL parameters (backend archive)
    if (estateId && clientId && address && owner) {
      showSuccess(
        "arvesti andmed on juba laetud. Palun sisestage praegune arvesti näit."
      );
      return;
    }

    try {
      const meterList = await searchMeters(meterNumber, true); // Only search active meters for removal

      if (!meterList.length) {
        showError("Ei leidnud aktiivset arvestit selle numbriga.");
        return;
      }

      // Find exact system_number match (fallback if backend returns multiple)
      let meter = meterList.find((m) => m.system_number === meterNumber);
      if (!meter) {
        // Fallback to first meter if no exact match found
        meter = meterList[0];
      }

      // Check if this meter has sub-meters (if backend provides this info)
      if (meter.has_sub_meters || meter.sub_meters_count > 0) {
        showError(
          `Sellel arvestil on alamarvesteid. Põhiarvestit ei saa eemaldada enne alamarvestite eemaldamist.`
        );
        return;
      }

      // Load estate data if we have estate ID but meter doesn't have full estate data
      let estateData = null;
      if (meter.estate && typeof meter.estate === "number") {
        // Estate is just an ID, we need to fetch full estate data
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/estates/${meter.estate}/`,
            {
              headers: {
                Authorization: `Token ${localStorage.getItem("apiToken")}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (response.ok) {
            estateData = await response.json();
          } else {
            console.error(
              "Estate fetch failed:",
              response.status,
              response.statusText
            );
          }
        } catch (error) {
          console.warn("Could not fetch estate details:", error);
        }
      } else if (meter.estate && typeof meter.estate === "object") {
        // Estate data is already included
        estateData = meter.estate;
      }

      // Set form data
      setEstateId(meter.estate || null);
      setMeterId(meter.id);

      if (estateData) {
        // Use estate data if available - prioritize agreement_partner like backend does
        const clientId = estateData.agreement_partner || null;
        setClientId(clientId);
        setAddress(estateData.address_str || "");

        // Load client name if we have a client ID
        if (clientId) {
          try {
            const clientResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/`,
              {
                headers: {
                  Authorization: `Token ${localStorage.getItem("apiToken")}`,
                  "Content-Type": "application/json",
                },
              }
            );
            if (clientResponse.ok) {
              const clientData = await clientResponse.json();
              setOwner(clientData.full_name || "");
            } else {
              console.error(
                "Client fetch failed:",
                clientResponse.status,
                clientResponse.statusText
              );
            }
          } catch (error) {
            console.warn("Could not fetch client details:", error);
            setOwner("");
          }
        } else {
          setOwner("");
        }
      } else {
        // Fallback to basic data
        setClientId(null);
        setAddress("");
        setOwner("");
      }

      // Find brand ID from model name - we need to map model name to brand ID
      if (meter.model) {
        const brandMatch = brandOptions.find((b) => b.name === meter.model);
        setBrand(brandMatch?.id?.toString() || "");
      } else {
        setBrand("");
      }

      // Find QNom ID from decimal value - we need to map meter.q_nom to QNom ID
      if (meter.q_nom) {
        const qnMatch = qnOptions.find(
          (q) => parseFloat(q.q_nom) === parseFloat(meter.q_nom)
        );
        setQn(qnMatch?.id?.toString() || "");
      } else {
        setQn("");
      }

      // Find Diameter ID from decimal value - we need to map meter.diameter to Diameter ID
      if (meter.diameter) {
        const diameterMatch = diameterOptions.find(
          (d) => parseFloat(d.diameter) === parseFloat(meter.diameter)
        );
        setDiameter(diameterMatch?.id?.toString() || "");
      } else {
        setDiameter("");
      }
      setReading(""); // Clear reading field so user must enter current reading
      setType(meter.type || "");
      setImages([]); // Always start with empty images array

      // Set last certification date if available
      if (meter.verification_date) {
        setLastCertification(new Date(meter.verification_date));
      }



      if (estateData) {
        showSuccess(
          "arvesti andmed laetud. Palun sisestage praegune arvesti näit."
        );
      } else {
        showSuccess(
          "arvesti põhiandmed laetud. Aadress ja omanik võivad olla tühjad - palun kontrollige need üle."
        );
      }
    } catch (error) {
      console.error("Search error:", error);
      showError("Aktiivse veemõõtja otsing nurjus. Palun kontrollige veemõõtja numbrit.");
    }
  };

  // Handle QR code scan for meter number
  const handleQRScan = async (scannedData) => {
    // Extract meter number from scanned data
    let meterNumber = scannedData.toString().trim();
    
    // If QR code contains additional data, extract meter number
    if (meterNumber.includes(':')) {
      const parts = meterNumber.split(':');
      meterNumber = parts[parts.length - 1].trim();
    }
    
    // Validate format (letters and digits)
    if (!isValidMeterNumber(meterNumber)) {
      showError('QR-koodist ei õnnestunud kehtivat arvesti numbrit lugeda');
      return;
    }
    
    // Set the meter number and automatically trigger search
    setMeterNumber(meterNumber);
    
    // Automatically search for the meter data
    try {
      const meterList = await searchMeters(meterNumber, true); // Only search active meters for removal

      if (!meterList.length) {
        showError("QR-koodist loetud arvesti numbriga ei leidnud ühtegi aktiivset arvestit.");
        return;
      }

      // Find exact system_number match (fallback if backend returns multiple)
      let meter = meterList.find((m) => m.system_number === meterNumber);
      if (!meter) {
        // Fallback to first meter if no exact match found
        meter = meterList[0];
      }

      // Check if this meter has sub-meters (if backend provides this info)
      if (meter.has_sub_meters || meter.sub_meters_count > 0) {
        showError(
          `Sellel arvestil on alamarvesteid. Põhiarvestit ei saa eemaldada enne alamarvestite eemaldamist.`
        );
        return;
      }

      // Load estate data if we have estate ID but meter doesn't have full estate data
      let estateData = null;
      if (meter.estate && typeof meter.estate === "number") {
        // Estate is just an ID, we need to fetch full estate data
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/estates/${meter.estate}/`,
            {
              headers: {
                Authorization: `Token ${localStorage.getItem("apiToken")}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (response.ok) {
            estateData = await response.json();

          } else {
            console.error(
              "Estate fetch failed:",
              response.status,
              response.statusText
            );
          }
        } catch (error) {
          console.warn("Could not fetch estate details:", error);
        }
      } else if (meter.estate && typeof meter.estate === "object") {
        // Estate data is already included
        estateData = meter.estate;
      }

      // Set form data
      setEstateId(meter.estate || null);
      setMeterId(meter.id);

      if (estateData) {
        // Use estate data if available - prioritize agreement_partner like backend does
        const clientId = estateData.agreement_partner || null;

        setClientId(clientId);
        setAddress(estateData.address_str || "");

        // Load client name if we have a client ID
        if (clientId) {
          try {
            const clientResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/`,
              {
                headers: {
                  Authorization: `Token ${localStorage.getItem("apiToken")}`,
                  "Content-Type": "application/json",
                },
              }
            );
            if (clientResponse.ok) {
              const clientData = await clientResponse.json();

              setOwner(clientData.full_name || "");
            } else {
              console.error(
                "Client fetch failed:",
                clientResponse.status,
                clientResponse.statusText
              );
            }
          } catch (error) {
            console.warn("Could not fetch client details:", error);
            setOwner("");
          }
        } else {
          setOwner("");
        }
      } else {
        // Fallback to basic data
        setClientId(null);
        setAddress("");
        setOwner("");
      }

      // Find brand ID from model name - we need to map model name to brand ID
      if (meter.model) {
        const brandMatch = brandOptions.find((b) => b.name === meter.model);
        setBrand(brandMatch?.id?.toString() || "");
      } else {
        setBrand("");
      }

      // Find QNom ID from decimal value - we need to map meter.q_nom to QNom ID
      if (meter.q_nom) {
        const qnMatch = qnOptions.find(
          (q) => parseFloat(q.q_nom) === parseFloat(meter.q_nom)
        );
        setQn(qnMatch?.id?.toString() || "");
      } else {
        setQn("");
      }

      // Find Diameter ID from decimal value - we need to map meter.diameter to Diameter ID
      if (meter.diameter) {
        const diameterMatch = diameterOptions.find(
          (d) => parseFloat(d.diameter) === parseFloat(meter.diameter)
        );
        setDiameter(diameterMatch?.id?.toString() || "");
      } else {
        setDiameter("");
      }
      setReading(""); // Clear reading field so user must enter current reading
      setType(meter.type || "");
      setImages([]); // Always start with empty images array

      // Set last certification date if available
      if (meter.verification_date) {
        setLastCertification(new Date(meter.verification_date));
      }



      if (estateData) {
        showSuccess(
          "QR-koodist loetud arvesti andmed laetud. Palun sisestage praegune arvesti näit."
        );
      } else {
        showSuccess(
          "QR-koodist loetud arvesti põhiandmed laetud. Aadress ja omanik võivad olla tühjad - palun kontrollige need üle."
        );
      }
    } catch (error) {
      console.error("QR Search error:", error);
      showError("QR-koodist loetud aktiivse arvesti otsing nurjus. Palun kontrollige veemõõtja numbrit.");
    }
  };

  const isValidMeterNumber = (value) =>
    typeof value === "string" && /^[A-Za-z0-9]+$/.test(value.trim());

  // Validate meter readings with up to 3 decimal places (supports comma and dot)
  const isValidReading = (v) => {
    return isValidDecimal(v, 3);
  };

  // Local date helpers to avoid timezone shifts when sending to backend
  const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
  const dateOnly = (d) => {
    if (!d) return "";
    const dd = new Date(d);
    return `${dd.getFullYear()}-${pad2(dd.getMonth() + 1)}-${pad2(dd.getDate())}`;
  };

  const validateMeterNumber = () => {
    if (!isValidMeterNumber(meterNumber)) {
      showError("Arvesti number võib sisaldada ainult tähti ja numbreid.");
      return false;
    }
    return true;
  };
  const hasReadingValue = (val) =>
    !(val === null || val === undefined || val.toString().trim() === "");
  const validateReading = (allowEmpty = false) => {
    if (!hasReadingValue(reading)) {
      if (allowEmpty) return true;
      showError(
        "Arvesti näit peab olema kehtiv number (kuni 3 kohta peale koma)."
      );
      return false;
    }
    if (!isValidReading(reading)) {
      showError(
        "Arvesti näit peab olema kehtiv number (kuni 3 kohta peale koma)."
      );
      return false;
    }
    return true;
  };

  // Save draft then navigate to Remove2
  const handleNext = async () => {
    if (!validateMeterNumber() || !validateReading()) return;

    // Safety check: prevent removing MAIN_GAUGE with sub-meters
    try {
      const meterList = await searchMeters(meterNumber, true);
      if (meterList && meterList.length) {
        let meter = meterList.find((m) => m.system_number === meterNumber) || meterList[0];
        if (meter?.type === 'MAIN_GAUGE' && (meter.has_sub_meters || (meter.sub_meters_count ?? 0) > 0)) {
          showError('Sellel põhiarvestil on alamarvesteid (alam/kastmine). Eemaldamine pole lubatud.');
          return;
        }
        // Ensure we persist meterId when available
        if (meter?.id && !meterId) setMeterId(meter.id);
      }
    } catch (e) {
      // If check fails, be conservative and block to avoid invalid removal
      console.warn('Sub-meter check failed, blocking removal as a precaution', e);
      showError('Ei saa jätkata: ei õnnestunud kontrollida alamarvesteid.');
      return;
    }

    const draft = {
      id,
      meterNumber,
      meterId,
      estate: estateId,
      client: clientId,
      address,
      owner,
      brand,
      qn,
      diameter,
      lastCertificationDate: dateOnly(lastCertification),
      reading,
      removalDate: dateOnly(removalDate),
      type,
      images,
      workType: "Eemaldamine",
      notes,
    };
    localStorage.setItem("removeData", JSON.stringify(draft));

    // Clear location.state so that when returning from Remove2, data is loaded from localStorage
    window.history.replaceState(null, '', window.location.pathname);

    navigate("/Remove2");
  };

  // helper: base64 → Blob (duplicated from Add1.js)
  function dataURLtoBlob(dataURL) {
    const [header, base64] = dataURL.split(",");
    const mime = header.match(/:(.*?);/)[1];
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  // Create or update on save
  const saveProgress = async (e) => {
    e.preventDefault();
    if (!validateMeterNumber() || !validateReading(true)) return;

    const readingValue = reading?.toString().trim();
    const readingProvided = hasReadingValue(reading);

    const payload = {
      action: "remove",
      client: clientId,
      estate: estateId,
      meter_brand: brand ? Number(brand) : null,
      meter_number: meterNumber?.toString().trim() || "",
      q_nom: qn ? Number(qn) : null,
      diameter: diameter ? Number(diameter) : null,
      type,
      last_certification_date: dateOnly(lastCertification),
      date: dateOnly(removalDate),
      status: "DRAFT",
      notes,
    };
    if (readingProvided) {
      payload.reading = Number(normalizeDecimal(readingValue));
    }

    try {
      const saved = id
        ? await updateMeterReport(id, payload)
        : await createMeterReport(payload);

      if (!saved || !saved.id) {
        showError("Backend ei tagastanud kehtivat vastust");
        return;
      }

      // Upload images separately (same logic as Add1.js)
      if (images && images.length > 0) {
        const reportPk = saved.id;
        let uploadIndex = 1;

        for (const src of images) {
          if (!src.startsWith("data:")) {
            // existing image (fetched for preview), skip it
            continue;
          }

          try {
            const blob = dataURLtoBlob(src);
            const filename = `report-${saved.number}-${uploadIndex}.png`;
            await uploadMeterReportImage(reportPk, blob, filename);
            uploadIndex++;
          } catch (imgErr) {
            console.error(`Pildi üleslaadimine ebaõnnestus (${uploadIndex}):`, imgErr);
          }
        }
      }

      showSuccess(id ? "Uuendatud edukalt." : "Salvestatud edukalt.");
      localStorage.removeItem("removeData");
      navigate("/works");
    } catch (err) {
      console.error("Salvestamisel viga:", err);
      showError("Salvestamisel viga.");
    }
  };

  const handleCapture = (img) => setImages((prev) => [...prev, img]);
  const handleDelete = (idx) =>
    setImages((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="max-w-4xl mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg border border-gray-300">
      <header className="bg-primary text-white text-center py-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Veemõõtja eemaldamine</h1>
      </header>
      <form className="space-y-6 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              Otsi arvesti numbrit
            </label>
            <div className="flex mb-4">
              <input
                type="text"
                value={meterNumber}
                onChange={(e) => setMeterNumber(e.target.value)}
                placeholder="Arvesti number"
                className="flex-grow p-3 border border-gray-300 rounded-l-md"
              />
              <button
                type="button"
                onClick={handleSearch}
                className="px-4 py-3 border border-gray-300 rounded-r-md bg-primary text-white hover:bg-primary-hover"
              >
                Otsi
              </button>
            </div>
            <div className="mb-4">
              <QRScanner 
                onScan={handleQRScan}
                placeholder="Skaneeri arvesti QR-kood"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Kinnistu
              </label>
              <input
                type="text"
                value={address}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-md bg-gray-100"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Omanik
              </label>
              <input
                type="text"
                value={owner}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-md bg-gray-100"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Arvesti näit
              </label>
              <input
                type="text"
                value={reading}
                onChange={(e) => setReading(e.target.value)}
                placeholder="Sisesta praegune arvesti näit"
                className="w-full p-3 border border-gray-300 rounded-md"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Eemaldamise kuupäev
              </label>
              <DatePicker
                selected={removalDate}
                onChange={(d) => {
                  d.setHours(0, 0, 0, 0);
                  setRemovalDate(d);
                }}
                dateFormat="dd-MM-yyyy"
                locale="et"
                className="w-full p-3 border border-gray-300 rounded-md"
              />
            </div>
          </div>
          {/* Right column */}
          <div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Arvesti mark
              </label>
              <select
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md"
              >
                <option value="" disabled>
                  Vali mark
                </option>
                {brandOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Qn/m³
              </label>
              <select
                value={qn}
                onChange={(e) => setQn(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md"
              >
                <option value="" disabled>
                  Vali siit
                </option>
                {qnOptions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.q_nom}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Läbimõõt
              </label>
              <select
                value={diameter}
                onChange={(e) => setDiameter(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md"
              >
                <option value="" disabled>
                  Vali läbimõõt
                </option>
                {diameterOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.diameter}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Viimane taatlemine
              </label>
              <DatePicker
                selected={lastCertification}
                onChange={(d) => {
                  d.setHours(0, 0, 0, 0);
                  setLastCertification(d);
                }}
                dateFormat="dd-MM-yyyy"
                locale="et"
                className="w-full p-3 border border-gray-300 rounded-md"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Vali tüüp
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md"
              >
                <option value="" disabled>
                  Vali siit
                </option>
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Lisa pilt
              </label>
              <CameraComponent onCapture={handleCapture} />
            </div>
          </div>
        </div>
        {/* Images preview */}
        <div className="mb-4">
          {images.map((src, idx) => (
            <div key={idx} className="relative inline-block m-2">
              <img
                src={src}
                alt={`Foto ${idx + 1}`}
                className="w-24 h-24 object-cover rounded-md"
              />
              <button
                type="button"
                onClick={() => handleDelete(idx)}
                className="absolute top-0 right-0 text-red-500 bg-white rounded-full p-1"
              >
                <FaTimesCircle size={20} />
              </button>
            </div>
          ))}
        </div>

        {/* Notes section */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-secondary mb-2">
            Märkused
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Lisa märkused..."
            rows={4}
            maxLength={4096}
            className="w-full p-3 border border-gray-300 rounded-md"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="bg-gray-300 text-black px-6 py-3 rounded-md hover:bg-gray-400"
          >
            Tagasi
          </button>
          <div className="space-x-2">
            <button
              type="button"
              onClick={saveProgress}
              className="bg-secondary text-white px-6 py-3 rounded-md hover:bg-secondary-hover"
            >
              Salvesta
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="bg-primary text-white px-6 py-3 rounded-md hover:bg-primary-hover"
            >
              Edasi
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
